import { FC, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@features/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { ContentTypeItem, ContentTypeFormData } from '@src/types/knowledgeBase';
import {
  getKnowledgeBaseTypes,
  createKnowledgeBaseType,
  updateKnowledgeBaseType,
  deleteKnowledgeBaseType,
} from '@src/services/knowledgeBase';
import { ContentTypeFormDialog } from '@features/admin/shared/ContentTypeFormDialog';
import { ConfirmDeleteGroupPopup } from '@features/admin/users/ConfirmDeleteGroupPopup';

interface KnowledgeBaseTypePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTypesChange?: () => void;
}

export const KnowledgeBaseTypePopup: FC<KnowledgeBaseTypePopupProps> = ({
  open,
  onOpenChange,
  onTypesChange,
}) => {
  const [types, setTypes] = useState<ContentTypeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<ContentTypeItem | null>(null);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');

  useEffect(() => {
    if (open) fetchTypes();
  }, [open]);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await getKnowledgeBaseTypes();

      setTypes(
        [...(res.data ?? [])].sort(
          (a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999)
        )
      );
    } catch {
      toast.error('Failed to load knowledge base types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelected(null);
    setFormMode('add');
    setFormOpen(true);
  };

  const handleEdit = (t: ContentTypeItem) => {
    setSelected(t);
    setFormMode('edit');
    setFormOpen(true);
  };

  const handleDelete = (t: ContentTypeItem) => {
    setSelected(t);
    setDeleteOpen(true);
  };

  const handleFormSubmit = async (data: ContentTypeFormData) => {
    try {
      if (formMode === 'add') {
        await createKnowledgeBaseType(data);
        toast.success('Type created');
      } else if (selected) {
        await updateKnowledgeBaseType(selected.id, data);
        toast.success('Type updated');
      }
      await fetchTypes();
      onTypesChange?.();
    } catch {
      toast.error(`Failed to ${formMode === 'add' ? 'create' : 'update'} type`);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;
    try {
      await deleteKnowledgeBaseType(selected.id);
      toast.success('Type deleted');
      await fetchTypes();
      setSelected(null);
      onTypesChange?.();
    } catch {
      toast.error('Failed to delete type');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[600px] max-w-[90vw] mx-auto max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-2xl text-center mb-[23px]">
              Manage Knowledge Base Types
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            {loading ? (
              <div className="text-center text-gray-400 py-8">Loading...</div>
            ) : types.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                No types found. Click &ldquo;Add Type&rdquo; to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {types.map((t) => (
                  <div
                    key={t.id}
                    className="border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base truncate">
                          {t.name}
                        </h4>
                        <p className="text-xs text-gray-400 mt-0.5">
                          key: {t.key}
                          {t.sort_order != null
                            ? ` · order: ${t.sort_order}`
                            : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          className="w-8 h-8"
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(t)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          className="w-8 h-8"
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(t)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-center gap-4">
              <Button
                className="px-6"
                size="sm"
                variant="lime"
                onClick={handleAdd}
              >
                Add Type
              </Button>
              <Button
                className="px-6"
                size="sm"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ContentTypeFormDialog
        initialData={
          selected
            ? {
                key: selected.key,
                name: selected.name,
                sort_order: selected.sort_order ?? 0,
                is_active: selected.is_active,
              }
            : null
        }
        mode={formMode}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDeleteGroupPopup
        groupTitle={selected?.name}
        open={deleteOpen}
        onConfirm={handleConfirmDelete}
        onOpenChange={setDeleteOpen}
      />
    </>
  );
};
